import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// --- Auth token yönetimi ---
export const TOKEN_KEY = "cr_auth_token";
export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};
export const setToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

// Her isteğe (varsa) Bearer token ekle
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Geçici ağ/gateway hatalarında (502/503/504 veya yanıtsız) GET isteklerini
// otomatik yeniden dene. Özellikle servis soğuk başlarken oluşan anlık 502'ler
// yüzünden "Analiz alınamadı" gibi hataların önüne geçer.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config || (config.method || "get").toLowerCase() !== "get") {
      return Promise.reject(error);
    }
    const status = error.response?.status;
    const isTransient = !error.response || [502, 503, 504].includes(status);
    config.__retryCount = config.__retryCount || 0;
    if (isTransient && config.__retryCount < 3) {
      config.__retryCount += 1;
      await new Promise((r) => setTimeout(r, 700 * config.__retryCount));
      return api(config);
    }
    return Promise.reject(error);
  },
);

export const encodeTag = (tag) => encodeURIComponent(tag);

export const clanApi = {
  config: () => api.get("/config").then((r) => r.data),
  list: () => api.get("/clans").then((r) => r.data),
  add: (tag) => api.post("/clans", { tag }).then((r) => r.data),
  remove: (tag) => api.delete(`/clans/${encodeTag(tag)}`).then((r) => r.data),
  status: (tag, refresh = true) =>
    api
      .get(`/clans/${encodeTag(tag)}/status`, { params: { refresh } })
      .then((r) => r.data),
  members: (tag, enrich = true, refresh = false) =>
    api
      .get(`/clans/${encodeTag(tag)}/members`, { params: { enrich, refresh } })
      .then((r) => r.data),
  history: (tag) =>
    api.get(`/clans/${encodeTag(tag)}/history`).then((r) => r.data),
  historyDetail: (tag, warDayId) =>
    api
      .get(`/clans/${encodeTag(tag)}/history/${encodeURIComponent(warDayId)}`)
      .then((r) => r.data),
  refresh: (tag) =>
    api.post(`/clans/${encodeTag(tag)}/refresh`).then((r) => r.data),
  analysisPeriods: (tag) =>
    api.get(`/clans/${encodeTag(tag)}/analysis/periods`).then((r) => r.data),
  analysis: (tag, params) =>
    api
      .get(`/clans/${encodeTag(tag)}/analysis`, { params })
      .then((r) => r.data),
  exemptions: (tag) =>
    api.get(`/clans/${encodeTag(tag)}/exemptions`).then((r) => r.data),
  addExemption: (tag, playerTag, name) =>
    api
      .post(`/clans/${encodeTag(tag)}/exemptions`, {
        player_tag: playerTag,
        name,
      })
      .then((r) => r.data),
  removeExemption: (tag, playerTag) =>
    api
      .delete(
        `/clans/${encodeTag(tag)}/exemptions/${encodeTag(playerTag)}`,
      )
      .then((r) => r.data),
  deleteHistory: (tag, warDayId) =>
    api
      .delete(
        `/clans/${encodeTag(tag)}/history/${encodeURIComponent(warDayId)}`,
      )
      .then((r) => r.data),
  weeklyStats: (tag, weeks = 4) =>
    api
      .get(`/clans/${encodeTag(tag)}/weekly-stats`, { params: { weeks } })
      .then((r) => r.data),
};

export const tournamentApi = {
  simulate: (players, seed = "trophies") =>
    api
      .post("/tournament/simulate", { players, seed })
      .then((r) => r.data),
  prepare: (players, seed = "trophies", since = null) =>
    api
      .post("/tournament/prepare", { players, seed, since })
      .then((r) => r.data),
  matchups: (players, since = null) =>
    api
      .post("/tournament/matchups", { players, since })
      .then((r) => r.data),
};

export const authApi = {
  login: (username, password) =>
    api.post("/auth/login", { username, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  changePassword: (old_password, new_password) =>
    api.post("/auth/change-password", { old_password, new_password }).then((r) => r.data),
  listUsers: () => api.get("/auth/users").then((r) => r.data),
  createUser: (payload) => api.post("/auth/users", payload).then((r) => r.data),
  updateUser: (id, payload) => api.put(`/auth/users/${id}`, payload).then((r) => r.data),
  updatePermissions: (id, permissions) =>
    api.put(`/auth/users/${id}/permissions`, { permissions }).then((r) => r.data),
  resetPassword: (id) =>
    api.post(`/auth/users/${id}/reset-password`).then((r) => r.data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`).then((r) => r.data),
  // Yönetim panelinde tüm kullanıcıların ekledikleri klanları listeler (kurucu)
  allClans: () => api.get("/admin/all-clans").then((r) => r.data),
  removeClanOwner: (tag, ownerId) =>
    api
      .delete(`/admin/all-clans/${encodeTag(tag)}`, { params: { owner_id: ownerId } })
      .then((r) => r.data),
};

export const playerApi = {
  get: (tag) => api.get(`/player/${encodeTag(tag)}`).then((r) => r.data),
  activity: (tag) =>
    api.get(`/player/${encodeTag(tag)}/activity`).then((r) => r.data),
  battles: (tag, limit = 25) =>
    api
      .get(`/player/${encodeTag(tag)}/battles`, { params: { limit } })
      .then((r) => r.data),
  search: (name) =>
    api.get("/players/search", { params: { name } }).then((r) => r.data),
  // Aşağıdakiler artık login gerektirdiği için misafir modunda kullanılmıyor;
  // frontend'de çağrılmıyor ama backward-compat için tanımlı bırakıldı.
  listSaved: () => api.get("/saved-players").then((r) => r.data),
  save: (tag, name) =>
    api.post("/saved-players", { tag, name }).then((r) => r.data),
  removeSaved: (tag) =>
    api.delete(`/saved-players/${encodeTag(tag)}`).then((r) => r.data),
};
